import Portal from '@material-ui/core/Portal';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import InputIcon from '@material-ui/icons/Input';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MenuIcon from '@material-ui/icons/Menu';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import { isAdmin as checkIsAdmin, publicIconPath, publicLogoPath } from '@orientaction/utils';
import clsx from 'clsx';
import { MouseEvent, useContext, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { ReactComponent as Groups } from '../../../../Asset/svg/groups.svg';
import FlagsSelect from '../../../../components/CountryFlagSelector/FlagsSelect';
import { UserProfilePhotoContext } from '../../../../Context/UserProfilePhotoContext';
import { redesignTheme } from '../../theme';
import RedesignImage from '../components/RedesignImage';
import { useAppHeaderStyles } from './AppHeader.styles';
import AppHeaderProfileMenu from './AppHeaderProfileMenu';
import HeaderNav from './HeaderNav';
import { useAppHeader } from './hooks/useAppHeader';
import { useHeaderNav } from './hooks/useHeaderNav';
import OAMatchDialog from './OAMatchDialog';

type AppHeaderProps = {
  showNav?: boolean;
  isJobLayout?: boolean;
  isUserConnected?: boolean;
};

const AppHeader = ({
  showNav = true,
  isJobLayout = false,
  isUserConnected = true,
}: AppHeaderProps) => {
  const history = useHistory();
  const headerRef = useRef<HTMLDivElement>(null);
  const { userProfilePhoto } = useContext<any>(UserProfilePhotoContext);

  const {
    user,
    language,
    onLanguageChange,
    open,
    anchorRef,
    handleToggle,
    handleClose,
    goToProfileEdit,
    logout,
    redirectUser,
    switchRole,
    canAccessOaMatch,
    disableMatch,
    showRecruitmentSwitch,
    isAdminUser,
    isRootUser,
    openDialogOAMatch,
    loadingPoppinsOAMatch,
    textPoppinOAMatch,
    checkOAMatchIfUserHasRefused,
    closeModalOA,
    closeAndRedirect,
    redirectToOAMatch,
    setOpenDialogOAMatch,
    mobileNavOpen,
    toggleMobileNav,
    closeMobileNav,
    mobileProfileOpen,
    toggleMobileProfile,
  } = useAppHeader();

  const navCollapseAt = isAdminUser
    ? redesignTheme.breakpoints.adminDesktop
    : redesignTheme.breakpoints.desktop;
  const classes = useAppHeaderStyles({ navCollapseAt });

  const nav = useHeaderNav({
    isAdmin: isAdminUser,
    isRoot: isRootUser,
    isJobLayout,
    service: user?.service,
  });

  const handleMobileNavToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    toggleMobileNav();
  };

  const initials = `${user?.firstname?.charAt(0).toUpperCase() || ''}${
    user?.lastname?.charAt(0).toUpperCase() || ''
  }`;

  const showBoutique = !isAdminUser && language !== 'GB';
  const showActionDivider = showBoutique || (!disableMatch && canAccessOaMatch);
  const hasMobileNav = showNav && nav.navItems.length > 0;

  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const scrollContainer = document.querySelector<HTMLElement>('[data-redesign-scroll-container]');
    const elements = [document.documentElement, document.body, scrollContainer].filter(
      (element): element is HTMLElement => Boolean(element)
    );
    const previousStyles = elements.map(element => ({
      element,
      overflow: element.style.overflow,
      overscrollBehavior: element.style.overscrollBehavior,
    }));

    elements.forEach(element => {
      element.style.overflow = 'hidden';
      element.style.overscrollBehavior = 'none';
    });

    return () => {
      previousStyles.forEach(({ element, overflow, overscrollBehavior }) => {
        element.style.overflow = overflow;
        element.style.overscrollBehavior = overscrollBehavior;
      });
    };
  }, [mobileNavOpen]);

  return (
    <>
      <div className={classes.headerShell} ref={headerRef} data-redesign-app-header={true}>
        <header className={clsx(classes.root, hasMobileNav && classes.rootWithMobileNav)}>
          {hasMobileNav && (
            <button
              type="button"
              className={classes.mobileNavButton}
              aria-label="Ouvrir la navigation"
              aria-expanded={mobileNavOpen}
              aria-controls="mobile-nav-panel"
              onClick={handleMobileNavToggle}>
              <MenuIcon className={classes.mobileNavIcon} />
            </button>
          )}

          <button
            type="button"
            className={clsx(classes.logoButton, hasMobileNav && classes.logoButtonMobileNav)}
            onClick={redirectUser}>
            <RedesignImage
              priority={true}
              src={`${publicLogoPath}/e-orientaction-redesign.png`}
              alt="E-Orientaction"
              className={clsx(classes.logo, hasMobileNav && classes.logoMobileNav)}
            />
          </button>

          {showNav && (
            <HeaderNav
              navItems={nav.navItems}
              activeTab={nav.activeTab}
              onNavClick={nav.handleNavClick}
              isAdmin={isAdminUser}
              openExit={nav.openExit}
              onExitCancel={nav.handleExitCancel}
              onExitClose={nav.handleExitClose}
              onExitConfirm={nav.handleExitConfirm}
            />
          )}

          {(showNav || (isUserConnected && user)) && (
            <div className={classes.actions}>
              {isUserConnected && user && (
                <>
                  {showBoutique && (
                    <button
                      type="button"
                      className={classes.boutiqueButton}
                      onClick={() => window.open('https://boutique-orientaction.com/', '_blank')}>
                      <RedesignImage
                        priority={true}
                        src={`${publicIconPath}/Shop.svg`}
                        alt=""
                        className={classes.boutiqueIcon}
                      />
                      Boutique
                    </button>
                  )}

                  {!disableMatch && canAccessOaMatch && (
                    <button
                      type="button"
                      className={classes.matchButton}
                      onClick={checkOAMatchIfUserHasRefused}>
                      <Groups className={classes.matchIcon} />
                      Match
                    </button>
                  )}

                  {showActionDivider && (
                    <span
                      className={clsx(
                        classes.divider,
                        hasMobileNav && classes.dividerDesktopOnly
                      )}
                      aria-hidden="true"
                    />
                  )}

                  {!isAdminUser && (
                    <>
                      <FlagsSelect
                        countries={['FR', 'GB']}
                        showOptionLabel={false}
                        showSelectedLabel={false}
                        fullWidth={false}
                        selected={language}
                        onSelect={onLanguageChange}
                        classes={{
                          button: classes.flagSelectButton,
                          popper: classes.flagSelectPopper,
                        }}
                      />
                      <span
                        className={clsx(
                          classes.divider,
                          hasMobileNav && classes.dividerDesktopOnly
                        )}
                        aria-hidden="true"
                      />
                    </>
                  )}

                  {showRecruitmentSwitch && !isJobLayout && (
                    <button
                      type="button"
                      className={classes.recruitmentButton}
                      onClick={() => history.replace('/admin/emploi/board')}>
                      Voir l&apos;espace recrutement
                    </button>
                  )}

                  {isJobLayout && (
                    <button
                      type="button"
                      className={classes.recruitmentButton}
                      onClick={() => history.replace('/admin/users-resultats')}>
                      {checkIsAdmin(user, [1, 2, 3])
                        ? 'Revenir à votre espace'
                        : 'Voir l\u2019espace classique'}
                    </button>
                  )}

                  <div
                    className={clsx(
                      classes.profileWrapper,
                      hasMobileNav && classes.profileDesktopOnly
                    )}>
                    <button
                      type="button"
                      ref={anchorRef}
                      className={classes.profileButton}
                      onClick={handleToggle}
                      aria-label="Ouvrir le menu utilisateur"
                      aria-expanded={open}
                      data-cy="menu-user-profile">
                      <span className={classes.profileAvatar}>
                        {userProfilePhoto?.length > 0 ? (
                          <RedesignImage
                            priority={true}
                            src={userProfilePhoto}
                            alt=""
                            className={classes.profilePhoto}
                          />
                        ) : (
                          initials
                        )}
                      </span>
                      <span
                        className={classes.profileName}>{`${user.firstname} ${user.lastname}`}</span>
                      {open ? (
                        <KeyboardArrowUpIcon className={classes.profileChevron} />
                      ) : (
                        <KeyboardArrowDownIcon className={classes.profileChevron} />
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </header>
      </div>

      <AppHeaderProfileMenu
        open={open}
        anchorRef={anchorRef}
        handleClose={handleClose}
        goToProfileEdit={goToProfileEdit}
        logout={logout}
        switchRole={switchRole}
        user={user}
      />

      {hasMobileNav && mobileNavOpen && (
        <Portal>
          <button
            type="button"
            className={classes.mobileNavBackdrop}
            aria-label="Fermer la navigation"
            onClick={closeMobileNav}
          />
          <nav
            id="mobile-nav-panel"
            className={classes.mobileNavPanel}
            aria-label="Navigation mobile">
            <div className={classes.mobileNavHeader}>
              <button
                type="button"
                className={classes.logoButton}
                onClick={() => {
                  redirectUser();
                  closeMobileNav();
                }}>
                <RedesignImage
                  priority={true}
                  src={`${publicLogoPath}/e-orientaction-redesign.png`}
                  alt="E-Orientaction"
                  className={classes.logo}
                />
              </button>
              <div className={classes.mobileNavHeaderActions}>
                {!isAdminUser && (
                  <FlagsSelect
                    countries={['FR', 'GB']}
                    showOptionLabel={false}
                    showSelectedLabel={false}
                    fullWidth={false}
                    selected={language}
                    onSelect={onLanguageChange}
                    classes={{
                      button: classes.flagSelectButton,
                      popper: classes.flagSelectPopper,
                    }}
                  />
                )}
                <button
                  type="button"
                  className={classes.mobileNavCloseButton}
                  aria-label="Fermer la navigation"
                  onClick={closeMobileNav}>
                  <CloseIcon />
                </button>
              </div>
            </div>
            <ul className={classes.mobileNavList}>
              {nav.navItems.map(item => (
                <li key={item.tabIndex} className={classes.mobileNavListItem}>
                  <button
                    type="button"
                    className={clsx(
                      classes.mobileNavItem,
                      nav.activeTab === item.tabIndex && classes.mobileNavItemActive
                    )}
                    onClick={() => {
                      nav.handleNavClick(item.tabIndex);
                      closeMobileNav();
                    }}>
                    {item.label}
                    <ChevronRightIcon className={classes.mobileNavChevron} />
                  </button>
                </li>
              ))}
            </ul>
            <div className={classes.mobileNavFooter}>
              {showBoutique && (
                <div className={classes.mobileNavBoutiqueWrap}>
                  <button
                    type="button"
                    className={classes.mobileNavBoutiqueButton}
                    onClick={() => window.open('https://boutique-orientaction.com/', '_blank')}>
                    <RedesignImage
                      priority={true}
                      src={`${publicIconPath}/Shop.svg`}
                      alt=""
                      className={classes.boutiqueIcon}
                    />
                    Boutique
                  </button>
                </div>
              )}
              {isUserConnected && user && (
                <div className={classes.mobileNavProfile}>
                  {mobileProfileOpen && (
                    <div className={classes.mobileProfileMenu} role="menu">
                      <button
                        type="button"
                        className={classes.mobileProfileMenuItem}
                        onClick={goToProfileEdit}>
                        <PersonOutlineIcon className={classes.mobileProfileMenuIcon} />
                        Mon compte
                      </button>
                      <button
                        type="button"
                        className={classes.mobileProfileMenuItem}
                        onClick={logout}
                        data-cy="logout-btn">
                        <InputIcon className={classes.mobileProfileMenuIcon} />
                        Déconnexion
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className={classes.mobileProfileButton}
                    aria-expanded={mobileProfileOpen}
                    onClick={toggleMobileProfile}>
                    <span className={classes.profileAvatar}>
                      {userProfilePhoto?.length > 0 ? (
                        <RedesignImage
                          priority={true}
                          src={userProfilePhoto}
                          alt=""
                          className={classes.profilePhoto}
                        />
                      ) : (
                        initials
                      )}
                    </span>
                    <span className={classes.mobileProfileName}>{`${user.firstname} ${user.lastname}`}</span>
                    {mobileProfileOpen ? (
                      <KeyboardArrowUpIcon className={classes.profileChevron} />
                    ) : (
                      <KeyboardArrowDownIcon className={classes.profileChevron} />
                    )}
                  </button>
                </div>
              )}
            </div>
          </nav>
        </Portal>
      )}

      {!disableMatch && (
        <OAMatchDialog
          open={openDialogOAMatch}
          loading={loadingPoppinsOAMatch}
          text={textPoppinOAMatch}
          onClose={() => setOpenDialogOAMatch(false)}
          onCancel={closeModalOA}
          onConfirm={redirectToOAMatch}
          onRedirect={closeAndRedirect}
        />
      )}
    </>
  );
};

export default AppHeader;
